import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import * as actions from '../../../../store';
import { useAppliedEvaluation } from '../../../../helpers/hooks/useEvaluation';
import { Card, CardContent, CardHeader, Divider, Tooltip, Typography } from '@material-ui/core';
import QualityCC from './QualityProjections/QualityCC';
import QualityMLO from './QualityProjections/QualityMLO';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import QualityFeatures from './QualityRevisionMode/QualityFeatures';
import { evalPNL, getTooltipText } from './utils/qualityUtils';
import { onClickQualityFeaturesChip } from './hooks/useQualityHandlers';
import { SymmetrySection } from './common/SymmetryChip';
import { ReviewerSelector } from '../Summary/SummaryCommon/ReviewerSelector';

const useStyles = makeStyles((theme) => ({
  card_content_container: {
    display: 'flex',
    padding: '0 !important',
    justifyContent: 'center',
    alignItems: 'center',
    height: '96%',
  },

  tooltip_text: {
    letterSpacing: '0.09rem',
    fontSize: '12px',
  },

  InfoIcon: {
    cursor: 'pointer',
  },

  container_quality_pgmi: {
    display: 'flex',
    alignItems: 'center',
    padding: 0,
    height: '100%',
    flexDirection: 'column',
    width: '100%',
    display: 'flex',
    justifyContent: 'space-evenly',
  },

  container_symmetry: {
    position: 'relative',
    minHeight: '25px',
    display: 'flex',
    width: ' 100%',
    justifyContent: 'center',
  },

  quality_head_container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    margin: '0',
    padding: '0 16px 0 8px',
    height: '38px',
    background: theme.palette.background.lighterBg,
    borderRadius: '5px 5px 0 0',
  },

  revision_quality_chips_container: {
    width: '100%',
    margin: '0',
    padding: '0',
    height: '38px',
    background: theme.palette.background.lighterBg,
    borderRadius: '5px 5px 0 0',
  },

  revision_info_icon: {
    position: 'absolute',
    top: '54px',
    right: '20px',
  },

  reviewer_titlepe: {
    marginLeft: '4px',
  },

  topRow: {
    display: 'flex',
    alignSelf: 'end',
    margin: '0 auto min(0.6vh, 0.6vw) auto',
    position: 'relative',
    maxHeight: '50%',
    width: '100%',
    justifyContent: 'center',
  },

  bottomRow: {
    display: 'flex',
    alignSelf: 'start',
    margin: 'min(0.6vh, 0.6vw) auto 0 auto',
    position: 'relative',
    maxHeight: '50%',
    width: '100%',
    justifyContent: 'center',
  },

  rcc: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: 'min(0.3vh, 0.3vw)',
    transform: 'scale(1)',
    transition: '0.3s',
    maxWidth: '50%',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    alignItems: 'flex-start',
    borderRadius: '5px',
  },

  lcc: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: 'min(0.3vh, 0.3vw)',
    transform: 'scale(1)',
    transition: '0.3s',
    maxWidth: '50%',
    flexDirection: 'row',
    width: '100%',
    height: '100%',
    alignItems: 'flex-start',
    borderRadius: '5px',
  },

  rmlo: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginRight: 'min(0.3vh, 0.3vw)',
    transform: 'scale(1)',
    transition: '0.3s',
    width: '100%',
    height: '100%',
    alignItems: 'flex-start',
    borderRadius: '5px',
  },

  lmlo: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginLeft: 'min(0.3vh, 0.3vw)',
    transform: 'scale(1)',
    transition: '0.3s',
    width: '100%',
    height: '100%',
    alignItems: 'flex-start',
    borderRadius: '5px',
  },

  '@keyframes appear': {
    '70%': {
      opacity: 0,
    },
    '100%': {
      opacity: 0.8,
    },
  },

  zoom: {
    transform: 'scale(2.02)',
    transition: '0.3s',
    zIndex: 80,
  },

  shrink: {
    transform: 'scale(0)',
    transition: '0.3s',
  },

  symmetry_icon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0',
    margin: '4px',
    justifyContent: 'space-around',
    cursor: 'help',
  },

  quality_title: {
    letterSpacing: '0.2rem',
    color: theme.palette.texts.primary,
    fontSize: theme.fonts.responsiveBig,
    whiteSpace: 'nowrap',
    fontWeight: 'bold',
  },
}));

const QualityView = () => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();

  const qualityInRevisionMode = useSelector((state) => state.labeling.qualityInRevisionMode);
  const { quality_features, quality_thresholds } = useSelector(
    (state) => state.global_settings.quality_system
  );
  const { inverted_view_position } = useSelector((state) => state.global_settings?.user_settings) || {};

  const { customQuality, localChangesQuality, qualityInDrawingMode, manualPnlChanges } = useSelector(
    (state) => state.labeling
  );
  const { zoomedProjection, tabsIndex } = useSelector((state) => state.gui);
  const {
    projections: projectionData,
    evaluations,
    applied_quality,
    shown_quality,
    confirmed_quality,
  } = useSelector((state) => state.results);

  const qualityInRevisionTab = tabsIndex === 2 && qualityInRevisionMode;
  const { symmetry_classes, pnl_diff_classes } = quality_features;
  const { pnl_diff_thresholds } = quality_thresholds;

  // Local state - simplified!
  const [qualityPopperOpen, setQualityPopperOpen] = useState(false);

  // Variables
  const shownQuality = shown_quality || applied_quality;
  const qualityEvaluation = useAppliedEvaluation('quality', shownQuality, qualityInRevisionMode);
  const projectionsType = ['rcc', 'lcc', 'rmlo', 'lmlo'];
  const isConfirmed = applied_quality === 'bbox' && confirmed_quality;
  const isRelabeled = confirmed_quality && applied_quality !== 'bbox';

  // Effects

  useEffect(() => {
    setQualityPopperOpen(false);
    dispatch(actions.setInitialImages());
    dispatch(actions.setDiagnosticsInRevisionMode(false));
    dispatch(actions.setDensityInRevisionMode(false));
    dispatch(actions.setShownQualityOverlayType(''));
    if (tabsIndex === 1) dispatch(actions.setQualityInRevisionMode(false));

    if (!shown_quality) dispatch(actions.setShownQuality(applied_quality));
  }, []);

  useEffect(() => {
    // Only auto-update PNL when the underlying measurements change and no manual changes exist
    const pnlClassRight = evalPNL(
      qualityEvaluation?.rmlo?.PectoNippleLength,
      qualityEvaluation?.rcc?.PectoNippleLength,
      pnl_diff_thresholds,
      pnl_diff_classes
    );
    if (qualityInRevisionMode && localChangesQuality && pnlClassRight && !manualPnlChanges?.rcc) {
      dispatch(actions.updateCustomQualityFeature('RCC', 'PNL', pnlClassRight));
    }

    const pnlClassLeft = evalPNL(
      qualityEvaluation?.lmlo?.PectoNippleLength,
      qualityEvaluation?.lcc?.PectoNippleLength,
      pnl_diff_thresholds,
      pnl_diff_classes
    );
    if (qualityInRevisionMode && localChangesQuality && pnlClassLeft && !manualPnlChanges?.lcc) {
      dispatch(actions.updateCustomQualityFeature('LCC', 'PNL', pnlClassLeft));
    }
  }, [
    qualityEvaluation?.rcc?.PectoNippleLength,
    qualityEvaluation?.lcc?.PectoNippleLength,
    qualityEvaluation?.rmlo?.PectoNippleLength,
    qualityEvaluation?.lmlo?.PectoNippleLength,
  ]);

  const classesMap = {
    rcc: [classes.rcc],
    lcc: [classes.lcc],
    rmlo: [classes.rmlo],
    lmlo: [classes.lmlo],
  };

  projectionsType.forEach((projection) => {
    if (zoomedProjection === projection) {
      classesMap[projection].push(classes.zoom);
    } else if (zoomedProjection) {
      classesMap[projection].push(classes.shrink);
    }
  });

  const { rcc: classesRcc, lcc: classesLcc, rmlo: classesRmlo, lmlo: classesLmlo } = classesMap;

  const rowConfig = inverted_view_position
    ? {
        top: ['rmlo', 'lmlo'],
        bottom: ['rcc', 'lcc'],
      }
    : {
        top: ['rcc', 'lcc'],
        bottom: ['rmlo', 'lmlo'],
      };

  const renderProjection = (proj, position) => {
    const isCC = proj.includes('cc');
    const Component = isCC ? QualityCC : QualityMLO;

    const projectionClasses = {
      rcc: classesRcc,
      lcc: classesLcc,
      rmlo: classesRmlo,
      lmlo: classesLmlo,
    };

    return (
      <div
        key={proj}
        className={projectionClasses[proj].join(' ')}
        style={{ margin: zoomedProjection && '0 6px', transformOrigin: position }}>
        <Component
          view_position={proj}
          result={qualityEvaluation?.[proj]}
          tooltipProj={(feature) =>
            projectionData?.[proj]
              ? getTooltipText(
                  proj,
                  feature,
                  evaluations,
                  applied_quality,
                  customQuality,
                  localChangesQuality
                )
              : t('ResultView.not_available')
          }
        />
      </div>
    );
  };

  return (
    <Card elevation={0} style={{ height: '100%' }}>
      <CardHeader
        style={{
          padding: 0,
          margin: 0,
          height: '36px',
          borderRadius: '5px 5px 0 0',
        }}
        title={
          qualityInRevisionTab ? (
            <div className={classes.revision_quality_chips_container}>
              <QualityFeatures />
            </div>
          ) : (
            <div className={classes.quality_head_container}>
              <Typography className={classes.quality_title} paragraph={false}>
                {t('b_box.b_quality')}
              </Typography>

              <ReviewerSelector
                type='quality'
                shownContent={shown_quality}
                appliedContent={applied_quality}
                isConfirmed={isConfirmed}
                isRelabeled={isRelabeled}
                classes={classes}
                onPopperClose={() => setQualityPopperOpen(false)}
              />

              {/* <Tooltip arrow title={t('Dashboard.pgmi_info')} placement='right-end'>
                <InfoIcon
                  className={classes.InfoIcon}
                  fontSize='small'
                  onClick={() => {
                    window.open(`/bbox/ifu_${i18n.language}`, '_blank');
                  }}
                />
              </Tooltip> */}
            </div>
          )
        }
      />

      <CardContent className={classes.card_content_container}>
        {qualityInRevisionTab && (
          <Tooltip
            arrow
            placement={'top-end'}
            title={
              <div>
                <Typography
                  style={{
                    letterSpacing: '0.1rem',
                    textTransform: 'uppercase',
                    fontSize: theme.fonts.responsiveMedium,
                    fontWeight: 'bold',
                  }}>
                  {t('ResultView.revision_mode')}
                </Typography>
                <Divider style={{ margin: '3% 0' }} />
                <Typography
                  style={{
                    letterSpacing: '0.1rem',
                    fontSize: theme.fonts.responsiveMedium,
                  }}>
                  - {t('ResultView.click_to_revise')}
                </Typography>

                <Typography
                  style={{
                    letterSpacing: '0.1rem',
                    fontSize: theme.fonts.responsiveMedium,
                  }}>
                  - {t('ResultView.click_to_draw')}
                </Typography>
                <Typography
                  style={{
                    letterSpacing: '0.1rem',
                    fontSize: theme.fonts.responsiveMedium,
                    marginTop: '6px',
                    color: theme.palette.secondary.main,
                  }}>
                  {t('ResultView.revise_disclaimer')}
                </Typography>
              </div>
            }>
            <InfoIcon className={classes.revision_info_icon} fontSize='small' />
          </Tooltip>
        )}
        <div className={classes.container_quality_pgmi}>
          {!zoomedProjection && (
            <div className={classes.container_symmetry} style={{ alignItems: 'flex-start' }}>
              {!!symmetry_classes && !qualityInDrawingMode && (
                <SymmetrySection
                  qualityEvaluation={qualityEvaluation}
                  onClickSymmetry={onClickQualityFeaturesChip}
                  projections={rowConfig.top}
                  symmetry_classes={symmetry_classes}
                  qualityInRevisionMode={qualityInRevisionMode}
                  classes={classes}
                  theme={theme}
                  t={t}
                  dispatch={dispatch}
                  actions={actions}
                />
              )}
            </div>
          )}

          <div className={classes.topRow}>
            {rowConfig.top.map((proj, i) => renderProjection(proj, i === 0 ? 'top left' : 'top right'))}
          </div>

          <div className={classes.bottomRow}>
            {rowConfig.bottom.map((proj, i) =>
              renderProjection(proj, i === 0 ? 'bottom left' : 'bottom right')
            )}
          </div>

          {!zoomedProjection && (
            <div className={classes.container_symmetry} style={{ alignItems: 'flex-end' }}>
              {!!symmetry_classes && !qualityInDrawingMode && (
                <SymmetrySection
                  qualityEvaluation={qualityEvaluation}
                  onClickSymmetry={onClickQualityFeaturesChip}
                  projections={rowConfig.bottom}
                  symmetry_classes={symmetry_classes}
                  qualityInRevisionMode={qualityInRevisionMode}
                  classes={classes}
                  theme={theme}
                  t={t}
                  dispatch={dispatch}
                  actions={actions}
                />
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default QualityView;
